import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Fab, Slide, Chip, LinearProgress, ListItemButton, Divider, useTheme } from '@mui/material';
import { ColorPicker, Icon } from '@ts/storybook';
import Link from '@components/Outputs/Link';
import { Theme } from '@mui/material/styles';
import useFetchElementType from '@utils/hooks/fetchData/useFetchElementType';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { IconTooltip } from '@components/Outputs';
import actStore from '@flux/actions/actStore';
import ChipEntity from '@components/Outputs/ChipEntity';
import { CMapElement } from '@models/maps/Map';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { KEYFLAVORS } from '@ts/ui-models';
import { useNavigate } from 'react-router';
import { getItems, getLocation } from '@services/DataBase/Element.service';
import { updateMapElement } from '@components/Maps/utils/map.utils';
import { cloneDeep } from 'lodash';
import Items, { defaultConfigComponent as ItConfigComponent } from '@pages/DataBase/Flavors/Items/Items';
import { Button, Grid, Typography, IAction } from '@ts/storybook';

import {
  CElementType,
  CLocation,
  formatDate,
  formatValue,
  CEntity,
  CView,
  CLabel,
  CStatusIndicator,
  CUnitFormatter,
} from '@ts/ui-models';
import { useTranslation } from 'react-i18next';
import { useMapContext } from '@pages/MapPage';

const useStyles = makeStyles<Theme, { theme: Theme }>(() => ({
  infoIcon: { verticalAlign: 'middle', marginLeft: '5px' },
  smallTooltip: {
    fontSize: 22,
  },
  stickyGrid: props => ({
    position: 'sticky',
    top: '-17px',
    zIndex: 1,
    backgroundColor: props.theme.palette.background.paper,
  }),
  container: props => ({
    overflowY: 'auto',
    overflowX: 'hidden',
    minWidth: 300,
    maxWidth: 300,
    borderLeft: '1px solid lightgray',
    backgroundColor: props.theme.palette.background.paper,
  }),
  wrapper: {
    color: 'black',
    padding: 16,
    textAlign: 'left',
  },
  divider: {
    height: 1,
    backgroundColor: '#888e99',
    marginTop: 8,
    marginBottom: 8,
  },
  title: {
    fontWeight: 'bold',
    color: 'black',
    margin: 5,
    marginLeft: 0,
  },
  infoItem: {
    margin: 5,
    marginLeft: 0,
    display: 'flex',
    alignItems: 'center',
  },
  entityLink: {
    color: 'black',
  },
  itemProperty: { flexDirection: 'row', display: 'flex', marginTop: '12px' },
  parsedReading: { display: 'flex', marginBottom: 10 },
  parsedReadingTypeNameStatus: {
    display: 'flex',
    flex: 4,
    alignItems: 'center',
  },
  displayColumn: {
    flexDirection: 'column',
    display: 'flex',
    flex: 3,
    alignItems: 'flex-end',
  },
  indicatorName: { marginLeft: 3 },
  /*   loading: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1500,
    backgroundColor: 'rgba(204, 204, 204, 0.6)',
  }, */
  infoLabel: {
    color: 'grey',
    fontSize: '0.7rem',
  },
}));

interface InfoSideBarProps {
  view: CView;
  elementsMapSelected: (CMapElement | CLocation)[];
  setElementsMapSelected?: React.Dispatch<React.SetStateAction<(CMapElement | CLocation)[]>>;
  elementSelected: CMapElement | CLocation | null;
  setElementSelected?: React.Dispatch<React.SetStateAction<CMapElement | CLocation | null>>;
  isQuickAccess?: boolean; //To hide close button
  //onSearchItem?: (v: CMapElement[] | null) => void;
  //onClickBackInfoBar?: () => void;
  //onClose?: () => void;
  onShowOtherItems?: () => void;
  onRefreshOneItem?: (itemID: string) => void;
}

const InfoSidebar = (props: InfoSideBarProps) => {
  const theme = useTheme();
  const classesStyle = useStyles({ theme });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { mapObserver } = useMapContext() ?? {};
  const readingTypeList = useAppSelector(state => state.global.readingTypesList);
  const unitsList = useAppSelector(state => state.global.unitsList);
  const unitFormatter = new CUnitFormatter(unitsList, readingTypeList);

  const { view, elementsMapSelected, setElementsMapSelected, elementSelected, setElementSelected } = props;
  const prevSelectedItem = useRef<CMapElement | CLocation | null>(null);
  const [itemsInsideByType, setItemsInsideByType] = useState<{ itemTypeId: string; count: number }[] | null>(null); //To know if the item is a location with itemTypes inside

  const [loading, setLoading] = useState(false);
  const [lastUpdateDate, setLastUpdateDate] = useState<string | null>(null);

  const ITEM_PROPERTIES = {
    currentLocation: 'Current Location',
    ownerOrganization: 'Organization',
    elementType: 'Type',
    logicalId: 'Logical ID',
    notes: 'Notes',
  };

  //#region Handlers
  const onClickOnItem = (item: CMapElement | CLocation) => {
    setElementSelected?.(item);
  };

  const onClickBackList = () => {
    setElementSelected?.(null);
  };

  const onCloseInfoSideBar = () => {
    setElementsMapSelected?.([]);
  };

  const onClickCountItemsInType = (elementType?: CElementType) => {
    if (!elementType) return;
    actStore.setActionTableV3({
      action: 'openModalExtraComp',
      component: Items,
      propsComponent: {
        title: ['Items inside', ' - ', elementSelected?.getName(), ' - ', 'Type', ': ', elementType.getName()],
        configComponent: {
          ...ItConfigComponent,
          where: { elementTypeId: elementType.getID() === 'Without type' ? null : elementType.getID() },
          filtersFetchData: {
            xlocs: {
              parentElementId: elementSelected?.getID(),
            },
          },
        },
      },
    });
  };
  //#endregion

  //#region Functions
  const callbackObserver = tUpdateMap => {
    const { type, updatedItem: item, data } = tUpdateMap;
    switch (type) {
      case 'updateAllItem':
        getEntityElementData(true);
        break;

      default:
        break;
    }
  };

  const elementTypeInsideInfoSideBar = useMemo(() => {
    if (!itemsInsideByType) {
      return elementsMapSelected
        .map(i => i.attachedDevices?.map(d => d.elementTypeId))
        .flat()
        .filter(id => id !== undefined) as string[];
    } else {
      return itemsInsideByType.flatMap(i => i.itemTypeId);
    }
  }, [elementSelected]);
  const listElementType = useFetchElementType(elementTypeInsideInfoSideBar);

  const actions = (): IAction[] => {
    const globalActions: IAction[] = [
      {
        id: 'openModalReading',
        label: 'Readings',
        icon: 'manage_search',
        onClick: () => actStore.setActionTableV3({ action: 'openModalReading', row: elementSelected }),
      },
      {
        id: 'openModalEvent',
        label: 'Events',
        icon: 'event_note',
        onClick: () => actStore.setActionTableV3({ action: 'openModalEvent', row: elementSelected }),
      },
    ];

    return globalActions;
  };

  const getEntityElementData = useCallback(
    async (needUpdate?: boolean) => {
      if (elementSelected && elementSelected instanceof CMapElement && (elementSelected.needUpdate || needUpdate)) {
        setLoading(true);
        const newDataElement = await getItems(dispatch, [elementSelected.getID()]);
        if (newDataElement?.[0] && elementSelected.unitFormatter) {
          const tMapElement = updateMapElement(
            newDataElement[0],
            elementSelected.unitFormatter,
            elementSelected,
            view.getReadingTypePosition()
          );
          setElementSelected?.(cloneDeep(tMapElement));
        }
        setLoading(false);
      }
    },
    [elementSelected]
  );

  const getEntityLocationData = useCallback(async () => {
    if (elementSelected && elementSelected instanceof CLocation && elementSelected.needUpdate) {
      setLoading(true);
      const copyDeep = cloneDeep(elementSelected);

      const newDataElement = await getItems(dispatch, [elementSelected.getID()]);
      if (newDataElement?.[0] && unitFormatter) {
        copyDeep.labels = newDataElement?.[0].xItemTags?.map(xl => new CLabel(xl.tag)) ?? [];
        copyDeep.statuses =
          newDataElement?.[0].currentStatus?.indicators.map(xi => new CStatusIndicator(xi, unitFormatter)) ?? [];
      }
      const newDataLocation = await getLocation(dispatch, elementSelected.getID());
      if (newDataLocation) {
        if (newDataLocation?.itemsInsideByType) {
          setItemsInsideByType(
            newDataLocation.itemsInsideByType.map((d, i) => ({
              id: d?.itemTypeId ?? 'Without type',
              itemTypeId: d?.itemTypeId ?? 'Without type',
              count: d.count,
            }))
          );
        }
        copyDeep.itemsInsideByType = newDataLocation.itemsInsideByType;
        copyDeep.needUpdate = false;
        setElementSelected?.(copyDeep);
      }
      setLastUpdateDate(formatDate(new Date()));
      setLoading(false);
    }
  }, [elementSelected]);
  //#endregion

  //#region Renders
  const renderItemLabel = () => {
    if (loading) return <LinearProgress />;
    if (!elementSelected) return;
    const itemLabels = elementSelected.labels;

    return (
      itemLabels &&
      itemLabels.map((label, i) => {
        if (!label) return;
        return <ChipEntity key={label.getID() + i} entity={label} />;
      })
    );
  };

  const renderItemLocations = () => {
    if (loading) return <LinearProgress />;
    if (!elementSelected) return;
    if (elementSelected.locations.length === 0) return <span className={classesStyle.indicatorName}>{t('None')}</span>;

    return elementSelected.locations?.map((location, i) => {
      const cLocation = new CLocation(location);

      return (
        <Chip
          key={location.getID() + i}
          sx={{ margin: '0.25rem', borderRadius: '5px!important', cursor: 'pointer' }}
          label={location.getName()}
          avatar={<Icon size={18} icon={KEYFLAVORS.location.icon} />}
          variant="outlined"
          size="small"
          onClick={() => {
            navigate(`/location/${cLocation.getID()}/view`, {
              state: { title: `${cLocation?.getLabelEntity()} | ${cLocation?.toString()}` },
            });
          }}
        />
      );
    });
    /* return elementSelected.locations?.map(location => {
      return (
        <p className={classesStyle.infoItem} key={location.getID()}>
          {location && location.getName()}
        </p>
      );
    }); */
  };
  const renderItemStatus = () => {
    if (loading) return <LinearProgress />;
    if (!elementSelected) return;
    const statuses = elementSelected.statuses;

    return statuses.map(status => {
      if (!status) return;

      //const status = createStatus(statuses[elementId][indicatorName], indicator, unitFormatter);

      if (!status.getValue) return;

      return (
        <span key={status.getID()} className={classesStyle.parsedReading}>
          <div className={classesStyle.parsedReadingTypeNameStatus}>
            <Icon icon={status.getIcon()} color={status.getColor} />
            <span className={classesStyle.indicatorName}>{status.name}</span>
          </div>
          <div className={classesStyle.displayColumn}>
            {status.type !== 'timestamp' && <span style={{ textAlign: 'right' }}>{status.customValue}</span>}
            <span>{status.getTimestamp}</span>
          </div>
        </span>
      );
    });
  };
  /*   const renderEvents = () => {
    if (!elementSelected) return;
    if (loading) return <LinearProgress />;
    const itemEvents = elementSelected.events;

    if (!itemEvents) return null;
    return itemEvents.slice(0, MAX_NUMBER_EVENTS).map((event: any) => {
      const eventType = eventTypeList?.find((eventType: any) => eventType.logicalId === event.eventTypeId);
      return (
        <p key={event.logicalId} className={classesStyle.infoItem}>
          <Link href={`/event/${event.logicalId}/view`} className={classesStyle.entityLink}>
            {moment(event.timestamp).format('h:mm A') +
              ' - (' +
              (eventType ? eventType.name : '') +
              ') ' +
              (event.name ? event.name : '')}
          </Link>
        </p>
      );
    });
  }; */

  const renderItemDevicesAttached = () => {
    if (!elementSelected) return;
    const itemDevicesAttached = elementSelected.attachedDevices;
    return itemDevicesAttached?.map(deviceAttached => {
      const getNameTypeDevice = listElementType?.find(el => el.getID() === deviceAttached.elementTypeId)?.getName();

      return (
        <Link
          key={deviceAttached.getID()}
          href={`/item/${deviceAttached.getID()}/view`}
          onClick={() => {
            navigate(`/item/${deviceAttached.getID()}/view`, {
              state: { title: `${deviceAttached?.getLabelEntity()} | ${deviceAttached?.toString()}` },
            });
          }}
        >
          <Typography text={[deviceAttached.getName(), ' (', getNameTypeDevice, ')']} />
        </Link>
      );
    });
  };

  const renderItemLastPosition = () => {
    if (!elementSelected || !(elementSelected instanceof CMapElement)) return;
    if (loading) return <LinearProgress />;
    const lastPosition = elementSelected.lastPosition;
    return (
      <span key={'lastPosition'} className={classesStyle.parsedReading}>
        <div className={classesStyle.parsedReadingTypeNameStatus}>
          <div></div>
          <span className={classesStyle.indicatorName}>Last position</span>
        </div>
        <div className={classesStyle.displayColumn}>
          <Typography
            variant="body2"
            className="text-right"
            text={formatValue(lastPosition?.data, null, view.getReadingTypePosition())}
          />
          <span>{formatDate(lastPosition?.timestamp)}</span>
        </div>
      </span>
    );
  };

  const renderItemInfo = () => {
    if (!elementSelected) return;

    let BodyEntity: JSX.Element | null = null;
    switch (elementSelected.constructor) {
      case CMapElement:
        BodyEntity = (
          <>
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Locations" />
            {renderItemLocations()}
            {renderItemLastPosition()}
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Statuses" />
            {renderItemStatus()}
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Labels" />
            {renderItemLabel()}
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Devices attached" />
            {renderItemDevicesAttached()}
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Data" />
            <Grid container spacing={1} pl={1}>
              {actions().map(action => {
                return renderActionIcon(action);
              })}
            </Grid>
            {/*  <Link onClick={() => actStore.setActionTableV3({ action: 'openModalReading', row: elementSelected })}>
          <Typography className={classesStyle.title} text={['Readings', ' (', 'Today', ')']} />
        </Link>
        {renderItemLastPosition()}
        <div className={classesStyle.divider} />
        <Link onClick={() => actStore.setActionTableV3({ action: 'openModalEvent', row: elementSelected })}>
          <Typography className={classesStyle.title} text={['Events', ' (', 'Today', ')']} />
        </Link>
        {renderEvents()} */}
          </>
        );
        break;
      case CLocation:
        BodyEntity = (
          <>
            <div className={classesStyle.divider} />
            {renderItemTypeInside()}
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Statuses" />
            {renderItemStatus()}
            <div className={classesStyle.divider} />
            <Typography className={classesStyle.title} text="Labels" />
            {renderItemLabel()}
          </>
        );
        break;
      default:
        break;
    }

    return (
      <Grid container spacing={1}>
        <Grid container size={{ xs: 12 }} justifyContent="space-between" alignItems="center">
          {elementsMapSelected.length > 1 && (
            <Fab color="primary" size="small" onClick={onClickBackList} sx={{ width: 24, height: 24, minHeight: 24 }}>
              <Icon icon="arrow_back" size={22} />
            </Fab>
          )}
          <Grid container size={{ xs: 'auto' }}>
            <Typography variant="h6" align="left" text="Details" />
            <IconTooltip color="#000000bd" text="Based on real-time data. Map filters do not apply." />
          </Grid>
          {!props.isQuickAccess && (
            <Fab color="primary" size="small" onClick={onCloseInfoSideBar} sx={{ width: 24, height: 24, minHeight: 24 }}>
              <Icon icon="close" size={20} />
            </Fab>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Link
            entity={elementSelected}
            href={`/item/${elementSelected.getID()}/view`}
            onClick={() => {
              navigate(`/item/${elementSelected.getID()}/view`, {
                state: { title: `${elementSelected?.getLabelEntity()} | ${elementSelected?.toString()}` },
              });
            }}
          >
            <Typography className={classesStyle.title} variant="body1" text={['Name', ': ', elementSelected.getName()]} />
          </Link>
          <Typography text={['ID', ': ', elementSelected.getVisualizationID()]} />
          <Typography className={classesStyle.itemProperty} text={['IDLog', ': ', elementSelected.getID()]} />
          {Object.keys(ITEM_PROPERTIES).map(
            (itemPropertyKey: string) =>
              elementSelected[itemPropertyKey]?.name && (
                <div className={classesStyle.itemProperty} key={itemPropertyKey + 'key'}>
                  <Typography text={[ITEM_PROPERTIES[itemPropertyKey], ': ', elementSelected[itemPropertyKey].name]} />
                </div>
              )
          )}
          {BodyEntity}
        </Grid>
      </Grid>
    );
  };

  const renderListEntityInMap = () => {
    return elementsMapSelected.map((item, index) => {
      if (!item) return;
      const iconName = item.getIcon();
      const iconColor = item.getIconColor?.();
      const tElementType = new CEntity(item.elementType);

      return (
        <React.Fragment key={index}>
          <ListItemButton onClick={() => onClickOnItem(item)} className="!px-0">
            <Grid container size={12} direction={'row'}>
              <Grid container size={10} direction={'column'}>
                <Grid
                  container
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Grid container size={'auto'} alignItems="center">
                    <Icon icon={iconName} color={iconColor} />
                    <Typography variant="body1" ml={1} text={[<b>{item.name}</b>]} />
                  </Grid>
                </Grid>
                <Typography variant="body1" text={[<b>ID</b>, ': ', item.getVisualizationID()]} />
                <Typography variant="body1" text={[<b>Type</b>, ': ', tElementType?.getName()]} />
              </Grid>
              {item instanceof CMapElement && <Grid container size={1}>
                <ColorPicker value={item.colorInMap} onChange={() => {}} isReadOnly />
              </Grid>}
            </Grid>
          </ListItemButton>
          {elementsMapSelected.length - 1 === index ? null : <Divider />}
        </React.Fragment>
      );
    });
  };

  const renderClusterInfo = () => {
    return (
      <Grid container direction="column" size={12} spacing={1}>
        <Grid container justifyContent="space-between" alignItems="center" className={classesStyle.stickyGrid}>
          <Typography variant="h6" align="left" text="List" />
          <Fab color="primary" size="small" onClick={onCloseInfoSideBar} sx={{ width: 24, height: 24, minHeight: 24 }}>
            <Icon icon="close" size={20} />
          </Fab>
        </Grid>
        {renderListEntityInMap()}
      </Grid>
    );
  };

  const renderActionIcon = (action: IAction) => {
    const { id, label, icon, onClick } = action;
    if (icon) {
      return (
        <Grid key={id} size={{ xs: 6 }}>
          <Button
            variant="outlined"
            label={label}
            size="small"
            onClick={onClick}
            endIcon={<Icon size={20} color="inherit" icon={icon} />}
          />
        </Grid>
      );
    }
  };

  const renderItemTypeInside = () => {
    if (loading) return <LinearProgress />;
    if (elementSelected && elementSelected instanceof CLocation) {
      return (
        <>
          <Grid container justifyContent="space-between" alignItems="center" pb={1}>
            <Grid>
              <Typography className={classesStyle.title} text="Items inside" />
            </Grid>
            {lastUpdateDate && (
              <Grid>
                <Typography className={classesStyle.infoLabel} text={lastUpdateDate} />
              </Grid>
            )}
          </Grid>
          {itemsInsideByType
            ?.sort((a, b) => b.count - a.count)
            ?.map((item, index) => {
              const elementType = listElementType?.find(el => el.getID() === item.itemTypeId);
              return (
                <Grid key={index} container justifyContent="space-between" pb={1}>
                  <Grid>
                    <Typography text={[elementType?.getName() ?? 'Loading...', ': ']} />
                  </Grid>
                  <Grid>
                    <Link onClick={() => onClickCountItemsInType(elementType)}>{item.count}</Link>
                  </Grid>
                </Grid>
              );
            })}
        </>
      );
    }
  };
  //#endregion

  useEffectAfterMount(() => {
    mapObserver?.subscribe(callbackObserver);
    if (elementSelected && elementSelected instanceof CMapElement) {
      getEntityElementData();
      elementSelected.featureSelected = true;
      elementSelected.notify('remarkFeature');
    }
    if (elementSelected && elementSelected instanceof CLocation) getEntityLocationData();

    prevSelectedItem.current = elementSelected;
    return () => {
      if (prevSelectedItem.current && prevSelectedItem.current instanceof CMapElement) {
        prevSelectedItem.current.featureSelected = false;
        prevSelectedItem.current.notify('remarkFeature');
      }
      setItemsInsideByType(null);
      mapObserver?.unsubscribe(callbackObserver);
    };
  }, [elementSelected?.getID()]);

  return (
    <Slide direction="left" in={Boolean(elementsMapSelected.length)} mountOnEnter unmountOnExit>
      <div>
        <Grid
          container
          direction="column"
          alignItems="flex-start"
          className={classesStyle.container}
          p={2}
          sx={{ height: '100%', zIndex: 1000 }}
        >
          {elementsMapSelected.length === 1 || elementSelected ? renderItemInfo() : renderClusterInfo()}
        </Grid>
      </div>
    </Slide>
  );
};

export default InfoSidebar;
